// ABOUTME: Modal for selecting which applicant studies to use when assessing an Academic entry requirement.
// ABOUTME: Allows university staff to select one or more studies from the applicant's education history.
import React, { useEffect, useState } from 'react';
import { Box, Button, ButtonGroup, Checkbox, Stack, Text } from '@chakra-ui/react';
import { Modal } from 'shared-components';
import { IApplicantOfferRequirementQualification, IStudy, IStudyHistory } from 'types';
import { getAcademicResultsFromStudyHistory } from '@/utilities/fulfilmentData';

interface StudyOption {
  id: string;
  study: IStudy;
}

interface SelectStudiesToAssessModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (studyIds: string[]) => void;
  studyHistory: IStudyHistory;
  qualifications: Record<string, IApplicantOfferRequirementQualification>;
  qualificationEntryId: string;
}

function buildStudyOptions(studyHistory: IStudyHistory): StudyOption[] {
  const options: StudyOption[] = [];

  if (studyHistory.highest && Object.keys(studyHistory.highest).length > 0) {
    options.push({ id: 'highest', study: studyHistory.highest });
  }

  if (studyHistory.previous) {
    for (const [studyId, study] of Object.entries(studyHistory.previous)) {
      options.push({ id: studyId, study: study as IStudy });
    }
  }

  return options;
}

function getActiveStudyIds(
  qualifications: Record<string, IApplicantOfferRequirementQualification>,
  qualificationEntryId: string,
): string[] {
  return Object.values(qualifications)
    .filter(
      q => !q.excluded && (q.id === qualificationEntryId || q.parentQualificationEntryId === qualificationEntryId),
    )
    .map(q => q.qualificationId ?? 'highest');
}

export function SelectStudiesToAssessModal({
  isOpen,
  onClose,
  onConfirm,
  studyHistory,
  qualifications,
  qualificationEntryId,
}: SelectStudiesToAssessModalProps): React.ReactElement {
  const studyOptions = buildStudyOptions(studyHistory);
  const [selectedStudyIds, setSelectedStudyIds] = useState<string[]>([]);
  const [showValidationError, setShowValidationError] = useState(false);

  // Initialise selection from the current qualification state each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedStudyIds(getActiveStudyIds(qualifications, qualificationEntryId));
      setShowValidationError(false);
    }
  }, [isOpen, qualifications, qualificationEntryId]);

  const handleToggle = (studyId: string): void => {
    setShowValidationError(false);
    setSelectedStudyIds(prev => (prev.includes(studyId) ? prev.filter(id => id !== studyId) : [...prev, studyId]));
  };

  const handleConfirm = (): void => {
    if (selectedStudyIds.length === 0) {
      setShowValidationError(true);
      return;
    }
    onConfirm(selectedStudyIds);
  };

  const handleClose = (): void => {
    setShowValidationError(false);
    onClose();
  };

  return (
    <Modal
      show={isOpen}
      onClose={handleClose}
      title="Select studies to assess"
      footer={
        <ButtonGroup spacing={3} size="lg">
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="solid" colorScheme="blue" onClick={handleConfirm}>
            Confirm
          </Button>
        </ButtonGroup>
      }
    >
      <Stack spacing={6}>
        <Text>
          Choose which studies you want to use to assess against this academic requirement. You can select more than
          one.
        </Text>
        <Box
          role="group"
          aria-labelledby="select-studies-label"
          aria-describedby={showValidationError ? 'select-studies-error' : undefined}
          aria-invalid={showValidationError || undefined}
        >
          <Text id="select-studies-label" fontWeight="medium" fontSize="lg" mb={3}>
            Select studies
          </Text>
          <Stack spacing={2}>
            {studyOptions.map(({ id, study }) => {
              const academicResults = getAcademicResultsFromStudyHistory(study);
              const isChecked = selectedStudyIds.includes(id);
              return (
                <Box
                  key={id}
                  border="2px solid"
                  borderColor={isChecked ? 'blue.300' : 'gray.200'}
                  borderRadius="md"
                  bg={isChecked ? 'blue.50' : 'white'}
                  px={4}
                  py={2}
                  cursor="pointer"
                  onClick={() => handleToggle(id)}
                >
                  <Checkbox
                    isChecked={isChecked}
                    onChange={() => handleToggle(id)}
                    onClick={e => e.stopPropagation()}
                    alignItems="flex-start"
                    spacing={3}
                    w="full"
                  >
                    <Box>
                      {academicResults.length > 0 ? (
                        <Stack spacing={0}>
                          {academicResults.map(({ text }, index) => (
                            <Text key={index} mb={0}>
                              {text}
                            </Text>
                          ))}
                        </Stack>
                      ) : (
                        <Text mb={0}>No qualifications provided</Text>
                      )}
                      {(study.institutionName || study.country) && (
                        <Text fontSize="sm" color="muted" mb={0}>
                          {[study.institutionName, study.country].filter(Boolean).join(', ')}
                        </Text>
                      )}
                    </Box>
                  </Checkbox>
                </Box>
              );
            })}
          </Stack>
          {showValidationError && (
            <Text id="select-studies-error" role="alert" color="red.500" mt={3} fontSize="sm">
              At least one study must be selected to assess the academic requirement.
            </Text>
          )}
        </Box>
      </Stack>
    </Modal>
  );
}
